import React from 'react';
import {Box, Grid, TextField, Typography, Button, Autocomplete} from "@mui/material";
import swal from "sweetalert";
import { PuffLoader } from "react-spinners";
import { css } from "@emotion/react";
import {getAllWellnessTopic, modifyWellnessChoice} from '../../../utilityFunctions/WellnessUtil';
import Colors from "../../../components/ui/Colors";
import NumberFormat from "react-number-format";
import { loaderAnimation } from '../../../Api';
import { useHistory } from "react-router-dom";

const choiceRequest = {};

const cssLoader = css`
  display: block;
  margin-left: auto;
  margin-right: auto;
`;

/**
 * ChangeWellnessChoice component provides an interface for modifying existing wellness choices.
 * Allows administrators to select a topic, question, and choice to modify its text and score.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.loginUser - The logged-in admin user with authentication token
 * @returns {JSX.Element} The ChangeWellnessChoice component
 */
export default function ChangeWellnessChoice(props) {

    const {loginUser} = props;

    let history = useHistory();

    const [loading, setLoading] = React.useState(false);

    const [wellnessTopics, setWellnessTopics] = React.useState(null);
    const [wellnessTopic, setWellnessTopic] = React.useState(null);
    const [wellnessQuestion, setWellnessQuestion] = React.useState("");
    const [wellnessChoice, setWellnessChoice] = React.useState(null);

    React.useEffect(() => {
        /**
         * Initializes the component by loading wellness topics.
         * 
         * @async
         * @returns {Promise<void>}
         */
        const loadInitialValues = async () => {
            await initialValues();
        }
        loadInitialValues();
    }, []);

    /**
     * Fetches all wellness topics from the server and updates the state.
     * Shows an error message if the operation fails.
     * 
     * @async
     * @returns {Promise<void>}
     */
    const initialValues = async () => {
        
        setLoading(true);
        const response = await getAllWellnessTopic(loginUser.jwtToken);
        setLoading(false);

        if(!response){
            swal({
                title: "Error",
                text: "Failed to get wellness topics",
                icon: "error",
                button: "OK",
            });
            return;
        }

        setWellnessTopics(response);
    }

    /**
     * Handles the selection of a wellness topic from the dropdown.
     * Updates the state with the selected topic.
     * 
     * @param {Event} e - The event object
     * @param {Object} value - The selected topic object
     * @returns {void}
     */
    const topicChange = (e, value) => {
        if(!value){
            return;
        }

        setWellnessTopic(value);
        
    };

    /**
     * Handles the selection of a wellness question/attribute from the dropdown.
     * Updates the state with the selected question.
     * 
     * @param {Event} e - The event object
     * @param {Object} value - The selected question object
     * @returns {void}
     */
    const questionChange = (e, value) => {
        if(!value){
            return;
        }

        setWellnessQuestion(value);
        
    };

    /**
     * Handles the selection of a wellness choice from the dropdown.
     * Updates the state and request object with the selected choice's details.
     * 
     * @param {Event} e - The event object
     * @param {Object} value - The selected choice object
     * @returns {void}
     */
    const choiceChange = (e, value) => {
        if(!value){
            return;
        }

        setWellnessChoice(value);
        setWellnessChoiceEditable(value.question);
        setWellnessChoiceScore(value.score);

        choiceRequest.choiceId = value.id;
        choiceRequest.score = value.score;
    }

    const [wellnessChoiceEditable, setWellnessChoiceEditable] = React.useState("");

    /**
     * Updates the choice text in both state and request object when the user types.
     * 
     * @param {Event} e - The change event from the text field
     * @returns {void}
     */
    const changeChoiceText = (e) => {
        setWellnessChoiceEditable(e.target.value);
        choiceRequest.question = e.target.value;
    }

    const [wellnessChoiceScore, setWellnessChoiceScore] = React.useState("");

    /**
     * Updates the choice score in both state and request object.
     * 
     * @param {Object} values - The NumberFormat values object containing the new score
     * @returns {void}
     */
    const wellnessScoreChange = (values) => {
        choiceRequest.score = values.floatValue;
        setWellnessChoiceScore(values.floatValue);
    }

    /**
     * Submits the modified wellness choice to the server.
     * Shows success or error messages based on the operation result.
     * Redirects to the handle_profile page on success.
     * 
     * @async
     * @returns {Promise<void>}
     */
    const submitChoiceChange = async () => {
        setLoading(true);
        const response = await modifyWellnessChoice(choiceRequest, loginUser.jwtToken);
        setLoading(false);

        if(!response){
            swal({
                title: "Error",
                text: "Failed to modify wellness choice",
                icon: "error",
                button: "OK",
            });
            return;
        }

        swal({
            title: "Success",
            text: "Wellness choice modified",
            icon: "success",
            button: "OK",
        });

        history.push("/handle_profile");
    }



    return (
        <Box sx={{display: "flex", justifyContent: "center", width: "100%", height: "100%"}}>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "95%",
                    height: "90%",
                    textAlign: "center",
                    marginTop: {xs: "1.3rem", sm: 0},
                }}
            >
                {loading ? loaderAnimation :
                    <Grid container direction="row" spacing={2}>
                    {wellnessTopics &&
                    <>
                        <Grid item xs={12} sm={12} md={12} sx={{display: "flex", justifyContent: "center"}}>
                            <Autocomplete
                                sx={{width: {xs: "90%", sm: "80%", md: "60%"}}}
                                options={wellnessTopics}
                                getOptionLabel={(option) => option.name}
                                clearOnEscape
                                autoComplete = {true}
                                noOptionsText = {"topic not found"}
                                fullWidth
                                onChange={topicChange}
                                renderInput={(params) => (
                                    <TextField  {...params} label="Topic" variant="standard" />
                                )}
                            />
                        </Grid>
                        {wellnessTopic && wellnessTopic.wellnessAttributes &&
                            <>
                                <Grid item xs={12} sm={12} md={12} sx={{display: "flex", justifyContent: "center"}}>
                                    <Autocomplete
                                        sx={{width: {xs: "90%", sm: "80%", md: "60%"}}}
                                        options={wellnessTopic.wellnessAttributes}
                                        getOptionLabel={(option) => option.name}
                                        clearOnEscape
                                        autoComplete = {true}
                                        noOptionsText = {"Question not found"}
                                        fullWidth
                                        onChange={questionChange}
                                        renderInput={(params) => (
                                            <TextField  {...params} label="Questions" variant="standard" />
                                        )}
                                    />
                                </Grid>
                                {wellnessQuestion && wellnessQuestion.wellnessChoices &&
                                    <>
                                        <Grid item xs={12} sm={12} md={12} sx={{display: "flex", justifyContent: "center"}}>
                                            <Autocomplete
                                                sx={{width: {xs: "90%", sm: "80%", md: "60%"}}}
                                                options={wellnessQuestion.wellnessChoices}
                                                getOptionLabel={(option) => option.question}
                                                clearOnEscape
                                                autoComplete = {true}
                                                noOptionsText = {"Question not found"}
                                                fullWidth
                                                onChange={choiceChange}
                                                renderInput={(params) => (
                                                    <TextField  {...params} label="Questions" variant="standard" />
                                                )}
                                            />
                                        </Grid>
                                        {wellnessChoice && wellnessChoiceEditable &&
                                        <>
                                            <Grid item xs={12} sm={12} md={12} sx={{marginTop: "1rem"}}>
                                                <TextField
                                                    variant="outlined"
                                                    fullWidth={true}
                                                    multiline
                                                    rows={3}
                                                    label={<Typography variant="body2">Choice</Typography>}
                                                    value={wellnessChoiceEditable}
                                                    onChange={(e)=> changeChoiceText(e)}
                                                    autoComplete="off"
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12} sx={{display: "flex", justifyContent: "center"}}>
                                                <NumberFormat
                                                    variant="standard"
                                                    style={{width: "40%", height: "50%"}}
                                                    customInput={TextField}
                                                    // prefix={'$ '}
                                                    // suffix={' years'}
                                                    type="text"
                                                    label={<Typography variant="body2">Score</Typography>}
                                                    thousandSeparator={'.'}
                                                    decimalSeparator={','}
                                                    autoComplete="off"
                                                    value={wellnessChoiceScore}
                                                    onValueChange={(values) => {
                                                        wellnessScoreChange(values);
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={12} md={12}>
                                                <Button onClick={submitChoiceChange} variant="contained" sx={{backgroundColor: Colors.primary, marginTop: "2rem",}}>
                                                    Submit
                                                </Button>
                                            </Grid>
                                        </>
                                        }
                                    </>
                                }
                            </>
                        }
                    </>
                    }
                    </Grid>
                }
            </Box>
        </Box>
    )
}
